/**
 * @fileoverview Browser push notification utilities using Push.js library.
 * Handles desktop push notifications with permission requests and error handling.
 */

import Push, { PushNotification } from "push.js";
import { PushNotificationType } from "./pushNotificationType";

const PUSH_PERMISSION_DENIED_ERROR = "PushError: permission request declined";
const PUSH_PERMISSION_ERROR_MESSAGE = "We need your permission to send live chat notifications";

/**
 * Creates and displays a desktop push notification.
 * Requests permission if not already granted.
 *
 * @param {PushNotificationType} message - Notification content with title, body, and icon
 * @param {number} [timeout=2000] - Auto-close timeout in milliseconds
 * @param {Function} [onClick] - Callback when notification is clicked
 * @param {Function} [onError] - Callback when notification fails
 * @returns {Promise<PushNotification | void>} The push notification instance or void
 *
 * @example
 * pushNotification(
 *   { title: 'New Message', body: 'You have a new chat' },
 *   3000,
 *   () => console.log('clicked'),
 *   (err) => console.error(err)
 * );
 */
export const pushNotification = (
  message: PushNotificationType,
  timeout: number = 2000,
  onClick: Function = () => {},
  onError: Function = () => {}
): Promise<PushNotification | void> => {
  const createPushNotif = () => {
    return Push.create(message.title, {
      body: message.body,
      icon: message.icon,
      timeout: timeout,
      onClick,
      onError,
    }).catch((error) => {
      onError(error);
    });
  };

  if (!Push.Permission.has()) {
    Push.Permission.request(
      () => {
        return createPushNotif();
      },
      () => {
        onError(PUSH_PERMISSION_DENIED_ERROR);
      }
    );
  }
  return createPushNotif();
};

/**
 * Displays an error notification when push notification fails.
 *
 * @param {any} $notify - Element UI notification instance
 * @param {string} [error] - Optional error message to display
 *
 * @example
 * notifyPushNotificationError(this.$notify, 'Permission denied');
 */
export const notifyPushNotificationError = ($notify: any, error?: string) => {
  $notify.error({
    position: "bottom-right",
    title: "Push Notification Error",
    message: `${PUSH_PERMISSION_ERROR_MESSAGE} Error: ${error}`,
  });
};

/**
 * Requests permission to display push notifications.
 * Only prompts if permission has not been previously granted.
 *
 * @param {Function} [acceptCb] - Callback when permission is granted
 * @param {Function} [rejectCb] - Callback when permission is denied
 *
 * @example
 * requestPushNotificationPermission(
 *   () => console.log('granted'),
 *   () => console.log('denied')
 * );
 */
export const requestPushNotificationPermission = (acceptCb = () => {}, rejectCb = () => {}) => {
  if (!Push.Permission.has()) {
    Push.Permission.request(
      () => {
        acceptCb();
      },
      () => {
        rejectCb();
      }
    );
  }
};

/**
 * Checks if push notification permission has been granted.
 *
 * @returns {boolean} True if notifications are enabled
 *
 * @example
 * if (isNotificationEnable()) {
 *   pushNotification({ title: 'Hello' });
 * }
 */
export const isNotificationEnable = () => {
  return Push.Permission.has();
};
